import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { ExperimentState } from '@/types';

interface ExperimentContextValue {
  state: ExperimentState | null;
  loadExperiment: (courseId: string, experimentId: string, initialData: Record<string, unknown>) => void;
  setCurrentPart: (partNum: number) => void;
  setCurrentStep: (stepNum: number) => void;
  updateData: (updates: Record<string, unknown>) => void;
  updatePartData: (partKey: string, updates: Record<string, unknown>) => void;
  completeStep: (stepId: string) => void;
  isStepCompleted: (stepId: string) => boolean;
  reset: () => void;
}

const ExperimentContext = createContext<ExperimentContextValue | null>(null);

export function ExperimentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ExperimentState | null>(null);

  const loadExperiment = useCallback((courseId: string, experimentId: string, initialData: Record<string, unknown>) => {
    // Try to restore saved state for this experiment
    const storageKey = `virtuallab_exp_${courseId}_${experimentId}`;
    let savedData = null;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        savedData = JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load experiment state:', e);
    }

    setState({
      courseId,
      experimentId,
      currentPart: savedData?.currentPart ?? 1,
      currentStep: savedData?.currentStep || 1,
      experimentData: savedData?.experimentData || initialData,
      completedSteps: savedData?.completedSteps || [],
    });
  }, []);

  const setCurrentPart = useCallback((partNum: number) => {
    setState(prev => {
      if (!prev) return prev;
      const newState = { ...prev, currentPart: partNum, currentStep: 1 };
      saveState(newState);
      return newState;
    });
  }, []);

  const setCurrentStep = useCallback((stepNum: number) => {
    setState(prev => {
      if (!prev) return prev;
      const newState = { ...prev, currentStep: stepNum };
      saveState(newState);
      return newState;
    });
  }, []);

  const updateData = useCallback((updates: Record<string, unknown>) => {
    setState(prev => {
      if (!prev) return prev;
      const newState = {
        ...prev,
        experimentData: { ...prev.experimentData, ...updates },
      };
      saveState(newState);
      return newState;
    });
  }, []);

  const updatePartData = useCallback((partKey: string, updates: Record<string, unknown>) => {
    setState(prev => {
      if (!prev) return prev;
      const currentPartData = (prev.experimentData[partKey] || {}) as Record<string, unknown>;
      const newState = {
        ...prev,
        experimentData: {
          ...prev.experimentData,
          [partKey]: { ...currentPartData, ...updates },
        },
      };
      saveState(newState);
      return newState;
    });
  }, []);

  const completeStep = useCallback((stepId: string) => {
    setState(prev => {
      if (!prev) return prev;
      if (prev.completedSteps.includes(stepId)) return prev;

      const stepNum = parseInt(stepId.split('-')[1]) || prev.currentStep;
      const newState = {
        ...prev,
        completedSteps: [...prev.completedSteps, stepId],
        currentStep: stepNum + 1,
      };
      saveState(newState);
      return newState;
    });
  }, []);

  const isStepCompleted = useCallback((stepId: string): boolean => {
    return state?.completedSteps.includes(stepId) || false;
  }, [state]);

  const reset = useCallback(() => {
    if (state) {
      const storageKey = `virtuallab_exp_${state.courseId}_${state.experimentId}`;
      localStorage.removeItem(storageKey);
    }
    setState(null);
  }, [state]);

  const saveState = (newState: ExperimentState) => {
    const storageKey = `virtuallab_exp_${newState.courseId}_${newState.experimentId}`;
    localStorage.setItem(storageKey, JSON.stringify(newState));
  };

  const value: ExperimentContextValue = {
    state,
    loadExperiment,
    setCurrentPart,
    setCurrentStep,
    updateData,
    updatePartData,
    completeStep,
    isStepCompleted,
    reset,
  };

  return (
    <ExperimentContext.Provider value={value}>
      {children}
    </ExperimentContext.Provider>
  );
}

export function useExperiment() {
  const context = useContext(ExperimentContext);
  if (!context) {
    throw new Error('useExperiment must be used within an ExperimentProvider');
  }
  return context;
}

export default ExperimentContext;
